/**
 * TASKNEIROKLYUCH-673: маппинг статусов E-Staff (state_id) → цветовая категория
 * для плашки статуса в таблице поиска и других местах.
 *
 * Маппим по state_id (стабильный код), не по name — имена в E-Staff могут
 * меняться, и подстрочный регистр/орфография ломали бы цвет.
 *
 * Категории:
 * - success      — финальные положительные (взят, оформление, оффер принят) и
 *                  положительные результаты этапов (одобрен ...)
 * - in_progress  — этап в процессе / запланирован / ждём решение
 * - rejected     — отказы (рекрутер/заказчик/самоотказ/уволен/чёрный список)
 * - reserve      — кадровый резерв
 * - neutral      — новый / неизвестный / не использован
 */

const SUCCESS = 'success';
const IN_PROGRESS = 'in_progress';
const REJECTED = 'rejected';
const RESERVE = 'reserve';
const NEUTRAL = 'neutral';

// state_id → категория. Имена статусов брались из dictionary
// /api/v1/dictionaries/candidate_states (60 шт).
const STATE_ID_TO_CATEGORY = {
  // ─── Финальные положительные ────────────────────────────────────────
  hire: SUCCESS,
  hire_paperwork: SUCCESS,
  hired_for_another_vacancy: SUCCESS,
  job_offer: SUCCESS,
  'job_offer:succeeded': SUCCESS,

  // ─── Положительные результаты этапов ────────────────────────────────
  'event_type_1:completion_state_02': SUCCESS,
  'event_type_3:completion_state_01': SUCCESS,
  'event_type_5:completion_state_01': SUCCESS,
  'event_type_11:succeeded': SUCCESS,
  'interview:succeeded': SUCCESS,
  'rr_interview:succeeded': SUCCESS,
  'rr_interview2:succeeded': SUCCESS,
  'rr_resume_review:succeeded': SUCCESS,
  'security_check:succeeded': SUCCESS,

  // ─── В процессе / запланировано ────────────────────────────────────
  'event_type_1:scheduled': IN_PROGRESS,
  'event_type_3:scheduled': IN_PROGRESS,
  'event_type_5:scheduled': IN_PROGRESS,
  'event_type_11:scheduled': IN_PROGRESS,
  interview: IN_PROGRESS,
  'interview:scheduled': IN_PROGRESS,
  'interview:peer_undecided': IN_PROGRESS,
  rr_interview: IN_PROGRESS,
  'rr_interview:scheduled': IN_PROGRESS,
  rr_interview2: IN_PROGRESS,
  'rr_interview2:scheduled': IN_PROGRESS,
  rr_resume_review: IN_PROGRESS,
  security_check: IN_PROGRESS,

  // ─── Отказы (финальные) ────────────────────────────────────────────
  reject: REJECTED,
  rr_reject: REJECTED,
  self_reject: REJECTED,
  dismiss: REJECTED,
  blacklist: REJECTED,
  vacancy_cancel: REJECTED,
  vacancy_close: REJECTED,
  'job_offer:withdrawn': REJECTED,

  // ─── Отказы / отмены этапов ────────────────────────────────────────
  'event_type_1:failed': REJECTED,
  'event_type_1:withdrawn': REJECTED,
  'event_type_1:call_failed': REJECTED,
  'event_type_3:completion_state_02': REJECTED, // стоп-лист
  'event_type_3:completion_state_03': REJECTED, // у заказчика внешн.
  'event_type_3:completion_state_04': REJECTED, // у заказчика внутр.
  'event_type_5:completion_state_02': REJECTED,
  'event_type_11:failed': REJECTED,
  'event_type_11:withdrawn': REJECTED,
  'event_type_11:cancelled': REJECTED,
  'interview:failed': REJECTED,
  'interview:withdrawn': REJECTED,
  'interview:cancelled': REJECTED,
  'rr_interview:failed': REJECTED,
  'rr_interview:withdrawn': REJECTED,
  'rr_interview:cancelled': REJECTED,
  'rr_interview2:failed': REJECTED,
  'rr_interview2:withdrawn': REJECTED,
  'rr_interview2:cancelled': REJECTED,
  'rr_resume_review:failed': REJECTED,
  'rr_resume_review:withdrawn': REJECTED,
  'security_check:failed': REJECTED,

  // ─── Резерв ────────────────────────────────────────────────────────
  reserve: RESERVE,

  // ─── Нейтральные ───────────────────────────────────────────────────
  new: NEUTRAL,
  unused: NEUTRAL,
};

// Категория → Tailwind classes для плашки (фон + текст).
const CATEGORY_CLASSES = {
  [SUCCESS]: 'bg-green-100 text-green-800',
  [IN_PROGRESS]: 'bg-blue-100 text-blue-800',
  [REJECTED]: 'bg-red-100 text-red-800',
  [RESERVE]: 'bg-yellow-100 text-yellow-800',
  [NEUTRAL]: 'bg-gray-100 text-gray-800',
};

/**
 * Подобрать CSS-классы плашки для статуса E-Staff.
 *
 * @param {Object|string|null} statusOrId
 *   - объект `{id, name}` (предпочтительно — id используется как ключ),
 *   - строка id (`'hire_paperwork'`),
 *   - null/undefined → нейтральный.
 * @returns {string} Tailwind классы (`'bg-green-100 text-green-800'` и т.п.).
 */
export function getEstaffStatusClasses(statusOrId) {
  if (!statusOrId) return CATEGORY_CLASSES[NEUTRAL];
  const id = typeof statusOrId === 'string' ? statusOrId : statusOrId.id;
  if (!id) return CATEGORY_CLASSES[NEUTRAL];
  // Защита от вариаций регистра между инстансами E-Staff (карта в lowercase).
  const category = STATE_ID_TO_CATEGORY[id] || STATE_ID_TO_CATEGORY[String(id).toLowerCase()] || NEUTRAL;
  return CATEGORY_CLASSES[category];
}

// Экспорт для тестов и других потребителей.
export const _categories = { SUCCESS, IN_PROGRESS, REJECTED, RESERVE, NEUTRAL };
export const _stateIdToCategory = STATE_ID_TO_CATEGORY;
